import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { callDownloadWebservice } from '../../hooks/api';

const PaymentReceipt = () => {
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const orderRef = searchParams.get("order_id") || location.state?.order_id || "N/A";
    const successParam = searchParams.get("success") || location.state?.success;
    let orderDate = searchParams.get("created_at") || location.state?.created_at;
    const merchantId = searchParams.get("merchant_id") || location.state?.merchantId || "";

    const isSuccess = String(successParam) === '1' || String(successParam) === 'true';

    const orderMessage = isSuccess
        ? 'Your payment was processed successfully.'
        : 'We encountered an issue while processing your payment. Please try again.';

    if (!orderDate || orderDate === 'N/A') {
        orderDate = new Date().toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    }

    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        // Push state to create a new history entry
        window.history.pushState(null, null, window.location.href);
        
        const handlePopState = (event) => {
            // Force the browser forward if the user attempts to go back
            window.history.forward();
        };
        
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const handleDownloadReceipt = () => {
        setIsDownloading(true);
        
        const reqData = {
            merchantId: parseInt(merchantId) || 0,
            orderSlug: orderRef,
            format: "pdf",
        };

        const successHandler = () => {
            setIsDownloading(false);
        };

        const errorHandler = (err) => {
            console.error('Download error:', err);
            alert('Failed to download receipt. Please try again.');
            setIsDownloading(false);
        };

        callDownloadWebservice('orders/receipt', reqData, `Receipt-${orderRef}.pdf`, successHandler, errorHandler, dispatch, navigate);
    };

    const styles = {
        page: {
            minHeight: '100vh',
            background: '#f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 16px',
            fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
        },
        card: {
            background: '#ffffff',
            borderRadius: '24px',
            boxShadow: '0 32px 80px rgba(0,0,0,0.18)',
            width: '100%',
            maxWidth: '460px',
            overflow: 'hidden',
            animation: 'slideUp 0.5s ease forwards',
        },
        cardHeader: {
            background: isSuccess
                ? 'linear-gradient(135deg, #00c853 0%, #00e676 100%)'
                : 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
            padding: '40px 32px 56px',
            textAlign: 'center',
            position: 'relative',
        },
        iconCircle: {
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.25)',
            border: '3px solid rgba(255,255,255,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '32px',
            color: '#fff',
        },
        headerTitle: {
            fontSize: '26px',
            fontWeight: '700',
            color: '#fff',
            margin: '0 0 8px',
            letterSpacing: '-0.3px',
        },
        headerSub: {
            fontSize: '14px',
            color: 'rgba(255,255,255,0.85)',
            margin: 0,
            lineHeight: '1.5',
        },
        wave: {
            position: 'absolute',
            bottom: '-1px',
            left: 0,
            width: '100%',
        },
        body: {
            padding: '32px 32px 40px',
        },
        sectionLabel: {
            fontSize: '11px',
            fontWeight: '700',
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '1.2px',
            marginBottom: '16px',
        },
        detailsBox: {
            background: '#f8fafc',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            marginBottom: '32px',
        },
        detailRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 20px',
            borderBottom: '1px solid #f1f5f9',
        },
        detailRowLast: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 20px',
        },
        detailLabel: {
            fontSize: '13px',
            color: '#64748b',
            fontWeight: '500',
        },
        detailValue: {
            fontSize: '13px',
            color: '#0f172a',
            fontWeight: '600',
            maxWidth: '220px',
            textAlign: 'right',
            wordBreak: 'break-all',
        },
        statusBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            background: isSuccess ? '#dcfce7' : '#fee2e2',
            color: isSuccess ? '#16a34a' : '#dc2626',
        },

        btn: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: '100%',
            padding: '15px 24px',
            background: isSuccess
                ? 'linear-gradient(135deg, #00c853 0%, #00e676 100%)'
                : 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)',
            color: '#fff',
            fontSize: '15px',
            fontWeight: '600',
            borderRadius: '14px',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'none',
            boxShadow: isSuccess
                ? '0 4px 16px rgba(0,200,83,0.35)'
                : '0 4px 16px rgba(100,116,139,0.35)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            opacity: isDownloading ? 0.7 : 1,
        },
    };

    return (
        <>
            <style>{`
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(40px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .pr-btn:hover {
                    transform: translateY(-2px) !important;
                    box-shadow: 0 8px 24px rgba(0,0,0,0.2) !important;
                    color: #fff !important;
                    text-decoration: none !important;
                }
            `}</style>
            <div style={styles.page}>
                <div style={styles.card}>
                    {/* Header */}
                    <div style={styles.cardHeader}>
                        <div style={styles.iconCircle}>
                            <i className={isSuccess ? 'fas fa-check' : 'fas fa-times'}></i>
                        </div>
                        <h1 style={styles.headerTitle}>
                            {isSuccess ? 'Payment Successful!' : 'Payment Failed'}
                        </h1>
                        <p style={styles.headerSub}>{orderMessage}</p>
                        {/* Wave SVG */}
                        <svg style={styles.wave} viewBox="0 0 1440 40" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                            <path d="M0,0 C360,40 1080,40 1440,0 L1440,40 L0,40 Z" fill="#ffffff"/>
                        </svg>
                    </div>

                    {/* Body */}
                    <div style={styles.body}>
                        <p style={styles.sectionLabel}>Transaction Details</p>

                        <div style={styles.detailsBox}>
                            <div style={styles.detailRow}>
                                <span style={styles.detailLabel}>Status</span>
                                <span style={styles.statusBadge}>
                                    <i className={isSuccess ? 'fas fa-check-circle' : 'fas fa-times-circle'} style={{ fontSize: '11px' }}></i>
                                    {isSuccess ? 'Paid' : 'Failed'}
                                </span>
                            </div>
                            <div style={styles.detailRow}>
                                <span style={styles.detailLabel}>Order Reference</span>
                                <span style={styles.detailValue}>{orderRef}</span>
                            </div>
                            <div style={styles.detailRowLast}>
                                <span style={styles.detailLabel}>Date & Time</span>
                                <span style={styles.detailValue}>{orderDate}</span>
                            </div>
                        </div>

                        {isSuccess && merchantId && (
                            <button className="pr-btn" style={styles.btn} onClick={handleDownloadReceipt} disabled={isDownloading}>
                                {isDownloading ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        Downloading...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-download"></i>
                                        Download Receipt
                                    </>
                                )}
                            </button>
                        )}

                    </div>
                </div>
            </div>
        </>
    );
};

export default PaymentReceipt;
